import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Loader2, Check, Globe, Calendar, ShieldX } from 'lucide-react';

const HERO_IMAGE = 'https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/21ukqol2_image.png';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [timezone, setTimezone] = useState('America/Vancouver');
  const [birthdate, setBirthdate] = useState('');
  const [timezones, setTimezones] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteValid, setInviteValid] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');

  useEffect(() => {
    if (!inviteCode) {
      setInviteValid(false);
      setInviteError('No invite link provided. Registration is by invitation only.');
      return;
    }

    apiClient.get(`/invites/validate/${inviteCode}`)
      .then(() => setInviteValid(true))
      .catch((err) => {
        setInviteValid(false);
        setInviteError(err.response?.data?.detail || 'Invalid or expired invite link.');
      });
  }, [inviteCode]);

  useEffect(() => {
    apiClient.get('/timezones')
      .then(res => setTimezones(res.data))
      .catch(() => {
        setTimezones([
          { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
          { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
          { value: 'America/New_York', label: 'Eastern Time (New York)' },
          { value: 'America/Chicago', label: 'Central Time (Chicago)' },
          { value: 'America/Denver', label: 'Mountain Time (Denver)' },
          { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
          { value: 'UTC', label: 'UTC' },
        ]);
      });
  }, []);

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inviteCode || !inviteValid) { toast.error('Invalid invite link'); return; }
    if (!name || !email || !password) { toast.error('Please fill in all fields'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }

    setLoading(true);
    try {
      const user = await register(name, email, password, timezone, birthdate || null, inviteCode);
      toast.success(`Welcome to RxGRIT, ${user.name}!`);
      setTimeout(() => { navigate('/my-actions', { replace: true }); }, 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
      setLoading(false);
    }
  };

  const inputCls = 'bg-white border-stone-300 focus:border-[#EFB342] focus:ring-1 focus:ring-[#EFB342] text-stone-900 placeholder:text-stone-400 h-12';
  const labelCls = 'text-stone-700 text-sm font-medium';

  // Validating invite
  if (inviteValid === null) {
    return (
      <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center text-stone-900">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#EFB342] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Validating invite link...</p>
        </div>
      </div>
    );
  }

  // Access Denied
  if (inviteValid === false) {
    return (
      <div className="min-h-screen bg-[#F5F4F1] flex items-center justify-center p-4 text-stone-900">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#EFB342]/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-[#EFB342]/30">
            <ShieldX className="text-[#B8871E]" size={40} />
          </div>
          <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-3">Members only</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-3 uppercase tracking-tight">Access denied</h1>
          <p className="text-stone-700 text-base md:text-lg mb-4 leading-relaxed">{inviteError}</p>
          <p className="text-stone-500 text-sm md:text-base mb-8 leading-relaxed">
            RxGRIT is a paid subscription service. If you&apos;ve purchased a subscription, please use the registration link provided in your confirmation email.
          </p>
          <div className="space-y-3 mb-16">
            <Button
              variant="outline"
              className="w-full border-stone-300 bg-white text-stone-800 hover:bg-stone-100 hover:text-stone-900 rounded-full h-12"
              onClick={() => navigate('/login')}
              data-testid="access-denied-login-btn"
            >
              Already have an account? Log in
            </Button>
            <a href="https://buildnutrition.ca/rxgrit" target="_blank" rel="noopener noreferrer">
              <Button className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider rounded-full h-12" data-testid="access-denied-learn-btn">
                Learn more about RxGRIT
              </Button>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F4F1] flex text-stone-900">
      {/* Left Side - Hero image (hidden on mobile) */}
      <div className="hidden lg:flex flex-1 items-start justify-center p-10 pt-16 bg-[#EEEAE1] border-r border-stone-200">
        <div className="relative w-full max-w-lg">
          <img
            src={HERO_IMAGE}
            alt="RxGRIT member — knowledge, habits, real change"
            className="w-full aspect-[4/5] object-cover rounded-3xl shadow-xl"
            style={{ filter: 'saturate(0.95) brightness(1.02)' }}
          />
          <div className="absolute bottom-5 left-5 right-5 md:left-auto md:right-6 md:max-w-xs bg-[#F5F4F1]/95 backdrop-blur-sm rounded-xl px-4 py-3 border border-stone-200 shadow-md">
            <h3 className="font-heading text-base font-bold text-stone-900 mb-1 leading-tight uppercase tracking-tight">28-Day Challenge</h3>
            <p className="text-stone-600 text-sm leading-snug">
              Build lasting habits, earn points, and climb the leaderboard alongside women in the same season.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-10" data-testid="register-brand-logo">
            <div className="w-11 h-11 bg-[#EFB342] rounded-xl flex items-center justify-center">
              <span className="text-black font-heading font-bold text-xl">Rx</span>
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-stone-900 tracking-wide leading-none">RxGRIT</h1>
              <p className="text-[10px] text-stone-500 uppercase tracking-widest mt-1">by BUILD Nutrition</p>
            </div>
          </Link>

          <div className="mb-8">
            <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-3">Create your account</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-3 uppercase tracking-tight leading-[1.05]">
              Get started.
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Create your account to join the challenge.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className={labelCls}>Full name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls}
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className={labelCls}>Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className={labelCls}>Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputCls} pr-12`}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {password && (
                <div className="mt-2 space-y-1">
                  {passwordRequirements.map((req, i) => (
                    <div key={i} className={`flex items-center gap-2 text-xs ${req.met ? 'text-green-600' : 'text-stone-500'}`}>
                      <Check size={12} />
                      <span>{req.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className={labelCls}>Confirm password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={inputCls}
                data-testid="register-confirm-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone" className={`${labelCls} flex items-center gap-2`}>
                <Globe size={14} />
                Your time zone
              </Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="bg-white border-stone-300 focus:border-[#EFB342] focus:ring-1 focus:ring-[#EFB342] text-stone-900 h-12">
                  <SelectValue placeholder="Select your timezone" />
                </SelectTrigger>
                <SelectContent className="bg-white border-stone-200">
                  {timezones.map((tz) => (
                    <SelectItem
                      key={tz.value}
                      value={tz.value}
                      className="text-stone-800 focus:bg-[#EFB342]/15 focus:text-stone-900"
                    >
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500">Used for daily check-in resets.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthdate" className={`${labelCls} flex items-center gap-2`}>
                <Calendar size={14} />
                Birthdate (optional)
              </Label>
              <Input
                id="birthdate"
                type="date"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                className={inputCls}
                data-testid="register-birthdate-input"
              />
              <p className="text-xs text-stone-500">We&apos;ll send you a special birthday message.</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider h-12 rounded-full mt-6"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  Create account
                  <ArrowRight className="ml-2" size={20} />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 mb-16 text-center text-stone-500 text-base">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-[#B8871E] hover:underline font-semibold"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
